import React, { useCallback, useContext, useState } from 'react';

import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import {
  SnackbarContext,
  SnackbarVariant,
  UserContext,
  User,
} from '@eas/common-web';
import LinearProgress from '@material-ui/core/LinearProgress';

import { DefaultDialog } from '../../../../components/dialog/vpuk-dialog/vpuk-default-dialog';
import { DialogContentProps } from '../../../../components/dialog/types';
import { Card } from 'models';
import { DialogContext } from '../../../../components/dialog/dialog-context';
import { PaymentStatusDialogContent } from '../../new-card/payment-status/payment-status';
import { fetchLicencePaymentLink } from './actions-api';
import { useIntl, FormattedMessage } from 'react-intl';
import { CardContext } from '../../../../components/card-context/card-context';

interface Values {
  card?: Card;
}

export function RevalidateCardDialogContent({
  initialValues,
}: DialogContentProps<Values>) {
  const { showSnackbar } = useContext(SnackbarContext);
  const [loading, setLoading] = useState(false);
  const card = initialValues?.card;
  const { open } = useContext(DialogContext);
  const { user } = useContext<UserContext<User>>(UserContext);
  const { loadCards } = useContext(CardContext);
  const intl = useIntl();

  const handleLicenceOrder = useCallback(
    async (card?: Card) => {
      if (!card || !card.newLicenceData || !user) {
        showSnackbar(
          intl.formatMessage({
            id: 'VPUK__MY_CARDS__REVALIDATE__MISSING_DATA',
          }),
          SnackbarVariant.ERROR
        );
        return;
      }

      try {
        setLoading(true);

        const body = {
          personId: card.newLicenceData.personId,
          firstName: card.newLicenceData.firstName,
          lastName: card.newLicenceData.lastName,
          cardId: card.id,
          cardType: card.cardType,
          licenceValidFrom: card.newLicenceData.licenceRightValidFrom,
          licenceValidTo: card.newLicenceData.licenceRightValidTo,
          licenceType: card.newLicenceData.licenceType,
          email: user.email.includes('@')
            ? user.email
            : `${user.email}@cuni.cz`,
        };

        const { paymentNumber, link } = await fetchLicencePaymentLink(body);
        window.open(link, '_blank');
        open({
          size: 'md',
          initialValues: {
            paymentLink: link,
            paymentNumber,
          },
          Content: PaymentStatusDialogContent,
          disableBackdropClick: true,
        });
      } catch (err) {
        showSnackbar(
          intl.formatMessage({
            id: 'VPUK__MY_CARDS__REVALIDATE__CANNOT_CREATE_PAYMENT',
          }),
          SnackbarVariant.ERROR
        );
      } finally {
        setLoading(false);
        loadCards();
      }
    },
    [user, showSnackbar, intl, open, loadCards]
  );

  return (
    <DefaultDialog
      title={intl.formatMessage({ id: 'VPUK__MY_CARDS__REVALIDATE' })}
      actions={
        <>
          <Button
            color="primary"
            onClick={() => handleLicenceOrder(card)}
            disabled={loading}
          >
            <FormattedMessage id="VPUK__MY_CARDS__REVALIDATE__CONFIRM_BUTTON" />
          </Button>
        </>
      }
    >
      {loading ? (
        <LinearProgress />
      ) : (
        <Typography>
          <FormattedMessage id="VPUK__MY_CARDS__REVALIDATE__CONFIRM" />{' '}
          {card?.newLicenceData?.price} CZK?
        </Typography>
      )}
    </DefaultDialog>
  );
}
